
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: () => void;
  onSave?: (apiKey: string) => void;
}

const ApiKeyDialog: React.FC<ApiKeyDialogProps> = ({
  open,
  onOpenChange,
  onContinue,
  onSave,
}) => {
  const [apiKey, setApiKey] = useState('');
  const { toast } = useToast();

  const handleContinue = () => {
    onContinue();
    onOpenChange(false);
    
    toast({
      title: "Continuing with server API key",
      description: "Using the application's OpenAI API key"
    });
  };

  const handleSave = () => {
    if (onSave && apiKey.trim()) {
      onSave(apiKey.trim());
      setApiKey('');
      toast({
        title: "API key saved",
        description: "Your OpenAI API key has been saved"
      });
    }
  };

  const isServerApiKey = !onSave;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isServerApiKey ? "Using Server-side OpenAI API Key" : "OpenAI API Key Required"}
          </DialogTitle>
          <DialogDescription>
            {isServerApiKey 
              ? "This application uses a server-side OpenAI API key for generating AI content."
              : "Please enter your OpenAI API key to use the AI generation features."
            }
          </DialogDescription>
        </DialogHeader>
        
        {isServerApiKey ? (
          <div className="grid gap-4 py-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="space-y-2 text-sm">
                <p>
                  The application is configured to use a server-side OpenAI API key. All requests to OpenAI will be made through the server.
                </p>
                <p>
                  If you experience any issues with AI generation, please contact the administrator.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="apiKey">OpenAI API Key</Label>
              <Input
                id="apiKey"
                type="password"
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Your API key is stored locally on your device and not sent to our servers.
              </p>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          {isServerApiKey ? (
            <Button onClick={handleContinue}>
              Continue
            </Button>
          ) : (
            <Button 
              onClick={handleSave}
              disabled={!apiKey.trim()}
            >
              Save API Key
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeyDialog;
